// src/utils/UniversalFileNaming.ts

// NOTE: This file implements a unified file-naming solution that standardizes
// naming conventions across the application (replacing separate logic in services
// like QRCodeFilename, VCardGenerator, etc.). It centralizes sanitization, enforces
// a consistent pattern, and provides built-in validation and parsing.
//
// Example Usage:
// ```typescript
// // Generate a vCard filename
// const vcardName = FileNaming.generate('vcard', {
//   userId: 'user123',
//   firstName: 'John',
//   lastName: 'Doe',
//   category: 'business'
// });
// // Output: user_user123_john_doe_business_20250104_abc123de.vcf
//
// // Generate a QR code filename
// const qrName = FileNaming.generate('qrcode', {
//   userId: 'user123',
//   firstName: 'John',
//   lastName: 'Doe',
//   category: 'marketing'
// });
// // Output: user_user123_john_doe_marketing_20250104_abc123de.svg
//
// // Generate a QR codes Metadata filename
// const metadataName = FileNaming.generate('qrcodesMetadata', {
//   userId: 'user123',
//   firstName: 'John',
//   lastName: 'Doe',
//   category: 'qrMetadata'
// });
// // Output: user_user123_john_doe_qrmetadata_20250104_abc123de.json
//
// // Parse an existing filename
// const parsed = FileNaming.parse('user_user123_john_doe_business_20250104_abc123de.vcf');
// // Output:
// // {
// //   userId: 'user123',
// //   firstName: 'john',
// //   lastName: 'doe',
// //   category: 'business',
// //   timestamp: '20250104',
// //   uniqueId: 'abc123de',
// //   extension: '.vcf'
// // }
// ```
//
// Implementation requires updating existing file naming logic across the codebase.

import { nanoid } from 'nanoid';

export type FileType =
  | 'vcard'
  | 'qrcode'
  | 'profile'
  | 'smarttag'
  | 'snowgear'
  | 'qrcodesMetadata'
  | 'svg';

/**
 * Updated to allow optional 'name' for a catch-all scenario,
 * plus optional firstName & lastName (instead of strictly required).
 */
interface BaseNamingParams {
  userId: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  category?: string;
  item?: string;
  timestamp?: Date;
  uniqueId?: string;
  extension?: string;
  prefix?: string;
}

type NamingParams = BaseNamingParams;

export class FileNaming {
  private static readonly DEFAULT_EXTENSION: Record<FileType, string> = {
    vcard: '.vcf',
    qrcode: '.svg',
    profile: '.jpg',
    smarttag: '.svg',
    snowgear: '.svg',
    qrcodesMetadata: '.json',
    svg: '.svg'
  };

  private static readonly FALLBACK_EXTENSION: string = '.dat'; // Catchall extension
  private static readonly USER_PREFIX: string = 'user';  // Added user prefix constant

  /**
   * Sanitizes a string for use in filenames
   */
  private static sanitize(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_') // Replace non-alphanumeric characters with '_'
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing '_'
  }

  /**
   * Helper method to handle either (firstName, lastName) or a single (name).
   */
  private static resolveNames(params: NamingParams): { firstName: string; lastName: string } {
    const { firstName, lastName, name } = params;

    // If firstName and lastName are provided, use them directly
    if (firstName && lastName) {
      return { firstName, lastName };
    }

    // Otherwise, if a single 'name' is available, split it
    if (name) {
      const [f, ...rest] = name.trim().split(/\s+/);
      return {
        firstName: f,
        lastName: rest.join(' ') || '',
      };
    }

    // Fallback if nothing is provided
    return { firstName: 'unknown', lastName: 'unknown' };
  }

  /**
   * Generates a unique identifier if not provided
   */
  private static getUniqueId(existingId?: string): string {
    return existingId || nanoid(8);
  }

  /**
   * Gets the timestamp string in YYYYMMDD format
   */
  private static getTimestamp(date?: Date): string {
    const timestamp = date || new Date();
    return timestamp.toISOString().split('T')[0].replace(/-/g, '');
  }

  /**
   * Gets the appropriate file extension
   */
  private static getExtension(fileType: FileType, providedExtension?: string): string {
    return providedExtension || this.DEFAULT_EXTENSION[fileType] || this.FALLBACK_EXTENSION;
  }

  /**
   * Generates a filename based on the file type and parameters
   * Follows the pattern: "user_[userId]_[firstName]_[lastName]_[category]_[item]_[date]_[uniqueId][extension]"
   */
  public static generate(fileType: FileType, params: NamingParams): string {
    const {
      userId,
      category = '',
      item = '',
      uniqueId,
      timestamp,
      extension,
      prefix,
    } = params;

    // Resolve names (firstName, lastName, or name)
    const { firstName, lastName } = this.resolveNames(params);

    // Sanitize all components
    const sanitizedUserId = this.sanitize(userId);
    const sanitizedFirstName = this.sanitize(firstName);
    const sanitizedLastName = this.sanitize(lastName);
    const sanitizedCategory = this.sanitize(category);
    const sanitizedItem = this.sanitize(item);
    
    // Generate other components
    const uniqueIdentifier = this.getUniqueId(uniqueId);
    const timeStamp = this.getTimestamp(timestamp);
    const fileExtension = this.getExtension(fileType, extension);

    // Build the parts array with the correct order
    const parts = [
      this.USER_PREFIX,  // Always start with 'user'
      sanitizedUserId,
      sanitizedFirstName,
      sanitizedLastName,
      sanitizedCategory,
      sanitizedItem,
      timeStamp,
      uniqueIdentifier
    ];

    // If prefix is provided (e.g., 'example'), insert it after category
    if (prefix) {
      const prefixIndex = 4; // After category
      parts.splice(prefixIndex, 0, this.sanitize(prefix));
    }

    // Filter out empty parts and join with underscores
    const filename = parts.filter(part => part !== '').join('_');
    return `${filename}${fileExtension}`;
  }

  /**
   * Parses a filename back into its components
   * Expects the pattern: "user_[userId]_[firstName]_[lastName]_[category]_[item]_[date]_[uniqueId][extension]"
   */
  public static parse(filename: string): {
    userId: string;
    firstName: string;
    lastName: string;
    category: string;
    item: string;
    timestamp: string;
    uniqueId: string;
    extension: string;
  } {
    const parts = filename.split('_');
    const extension = filename.substring(filename.lastIndexOf('.'));
    
    // Ensure we have at least the minimum required parts
    if (parts.length < 8) {
      throw new Error('Invalid filename format');
    }

    // Extract components based on the pattern
    const [prefix, userId, firstName, lastName, category, item, timestamp, uniqueIdWithExt] = parts;
    
    // Validate the user prefix
    if (prefix !== this.USER_PREFIX) {
      throw new Error('Invalid filename: must start with "user_"');
    }

    // Extract uniqueId without extension
    const uniqueId = uniqueIdWithExt.substring(0, uniqueIdWithExt.indexOf('.'));

    return {
      userId,
      firstName,
      lastName,
      category,
      item,
      timestamp,
      uniqueId,
      extension
    };
  }

  /**
   * Validates if a filename follows the naming convention
   */
  public static validate(filename: string): boolean {
    const parsed = this.parse(filename);
    if (!parsed) return false;

    // Validate timestamp format YYYYMMDD
    const timestampRegex = /^\d{8}$/;
    if (!timestampRegex.test(parsed.timestamp)) return false;

    // Validate uniqueId: exactly 8 alphanumeric characters
    const uniqueIdRegex = /^[a-z0-9]{8}$/i;
    if (!uniqueIdRegex.test(parsed.uniqueId)) return false;

    // Validate extension is present
    if (!parsed.extension) return false;

    return true;
  }
}
